<template>
  <div class="ps-page">
    <ps-top-bar :back-label="$t('contact.back')" />

    <main class="wrap contact-main">
      <!-- heading -->
      <div class="contact-head">
        <p class="ps-eyebrow">{{ $t("contact.label") }}</p>
        <h1 class="ps-title">
          {{ $t("contact.title") }}<br />
          <span class="contact-head__highlight">{{
            $t("contact.titleHighlight")
          }}</span>
        </h1>
        <p class="contact-head__sub">{{ $t("contact.sub") }}</p>
      </div>

      <!-- quick channel cards -->
      <div class="contact-channels">
        <a
          href="https://wa.me/8562028492947"
          target="_blank"
          rel="noopener"
          class="contact-channel card-hover"
        >
          <span class="contact-channel__icon contact-channel__icon--green"
            ><v-icon size="26">fab fa-whatsapp</v-icon></span
          >
          <h3 class="contact-channel__title">{{
            $t("contact.whatsappTitle")
          }}</h3>
          <p class="contact-channel__desc">{{
            $t("contact.whatsappDesc")
          }}</p>
        </a>
        <a
          href="https://www.facebook.com/pos.devla.la"
          target="_blank"
          rel="noopener"
          class="contact-channel card-hover"
        >
          <span class="contact-channel__icon contact-channel__icon--brand"
            ><span class="contact-channel__fb">f</span></span
          >
          <h3 class="contact-channel__title">{{
            $t("contact.facebookTitle")
          }}</h3>
          <p class="contact-channel__desc">{{
            $t("contact.facebookDesc")
          }}</p>
        </a>
        <a
          href="https://www.tiktok.com/@possmart_devla"
          target="_blank"
          rel="noopener"
          class="contact-channel card-hover"
        >
          <span class="contact-channel__icon contact-channel__icon--ink"
            ><v-icon size="26">fal fa-music</v-icon></span
          >
          <h3 class="contact-channel__title">{{
            $t("contact.tiktokTitle")
          }}</h3>
          <p class="contact-channel__desc">{{
            $t("contact.tiktokDesc", { at: "@" })
          }}</p>
        </a>
      </div>

      <!-- form + info -->
      <div class="contact-grid">
        <!-- form -->
        <div class="contact-form">
          <h3 class="contact-form__title">{{ $t("contact.formTitle") }}</h3>
          <p class="contact-form__sub">{{ $t("contact.formSub") }}</p>
          <form class="contact-form__body" novalidate @submit.prevent="submitForm">
            <div class="contact-form__row">
              <input
                v-model="form.name"
                type="text"
                required
                :placeholder="$t('contact.namePh')"
                class="ps-input"
                :class="{ 'ps-input--error': errors.name }"
              />
              <input
                v-model="form.email"
                type="email"
                required
                :placeholder="$t('contact.emailPh')"
                class="ps-input"
                :class="{ 'ps-input--error': errors.email }"
              />
            </div>
            <input
              v-model="form.phone"
              type="tel"
              :placeholder="$t('contact.phonePh')"
              class="ps-input"
            />
            <input
              v-model="form.subject"
              type="text"
              required
              :placeholder="$t('contact.subjectPh')"
              class="ps-input"
              :class="{ 'ps-input--error': errors.subject }"
            />
            <textarea
              v-model="form.message"
              rows="4"
              required
              :placeholder="$t('contact.messagePh')"
              class="ps-input"
              :class="{ 'ps-input--error': errors.message }"
            ></textarea>
            <p v-if="formError" class="contact-form__error">{{ formError }}</p>
            <button
              type="submit"
              class="ps-btn ps-btn--brand ps-btn--block contact-form__submit"
              :disabled="submitting"
            >
              {{ submitting ? "…" : $t("contact.submit") }}
            </button>
          </form>
        </div>

        <!-- info cards -->
        <div class="contact-info">
          <div class="contact-info-card card-hover">
            <span class="contact-info-card__icon"
              ><v-icon size="20">fal fa-map-pin</v-icon></span
            >
            <div>
              <h4 class="contact-info-card__title">{{
                $t("contact.officeTitle")
              }}</h4>
              <p class="contact-info-card__desc">{{
                $t("contact.officeDesc")
              }}</p>
            </div>
          </div>
          <div class="contact-info-card card-hover">
            <span class="contact-info-card__icon"
              ><v-icon size="20">fal fa-clock</v-icon></span
            >
            <div>
              <h4 class="contact-info-card__title">{{
                $t("contact.hoursTitle")
              }}</h4>
              <p class="contact-info-card__desc">{{
                $t("contact.hoursDesc")
              }}</p>
            </div>
          </div>
          <div class="contact-info-card card-hover">
            <span class="contact-info-card__icon"
              ><v-icon size="20">fal fa-envelope</v-icon></span
            >
            <div>
              <h4 class="contact-info-card__title">{{
                $t("contact.emailTitle")
              }}</h4>
              <p class="contact-info-card__desc">{{
                $t("contact.emailDesc", { at: "@" })
              }}</p>
            </div>
          </div>
          <div class="contact-info-card card-hover">
            <span class="contact-info-card__icon contact-info-card__icon--phone"
              ><v-icon size="20">fal fa-phone</v-icon></span
            >
            <div>
              <h4 class="contact-info-card__title">{{ $t("phone") }}</h4>
              <p class="contact-info-card__desc">020 28492947</p>
            </div>
          </div>
          <div class="contact-info-cta">
            <p class="contact-info-cta__text">{{ $t("contact.ctaTitle") }}</p>
            <router-link to="/register" class="contact-info-cta__btn">{{
              $t("contact.ctaBtn")
            }}</router-link>
          </div>
        </div>
      </div>

      <!-- map -->
      <div class="contact-map">
        <div class="contact-map__title">
          <v-icon size="18" color="primary">fal fa-map-marker-alt</v-icon>
          {{ $t("find_us") }}
        </div>
        <iframe
          :src="mapEmbedUrl"
          width="100%"
          height="360"
          style="border: 0"
          allowfullscreen=""
          loading="lazy"
          referrerpolicy="no-referrer-when-downgrade"
        ></iframe>
      </div>

      <!-- hours + response -->
      <div class="contact-bottom">
        <div class="contact-bottom__card">
          <div class="contact-bottom__title">
            <v-icon size="16" color="primary">fal fa-clock</v-icon>
            {{ $t("business_hours") }}
          </div>
          <div class="contact-bottom__hours">
            <div
              v-for="(hour, index) in businessHours"
              :key="index"
              class="contact-bottom__hour"
            >
              <span>{{ $t(hour.dayKey) || hour.dayKey }}</span>
              <span class="contact-bottom__hour-time">{{
                hour.time === "closed" ? $t("closed") || "ປິດ" : hour.time
              }}</span>
            </div>
          </div>
        </div>
        <div class="contact-bottom__card">
          <div class="contact-bottom__title">
            <v-icon size="16" color="primary">fal fa-comments</v-icon>
            {{ $t("response_time") }}
          </div>
          <p class="contact-bottom__text">
            {{ $t("response_time_description") }}
          </p>
          <span class="contact-bottom__chip">
            <v-icon size="12" left>fal fa-check-circle</v-icon>
            {{ $t("usually_responds") }}
          </span>
        </div>
      </div>
    </main>

    <footer class="contact-foot">
      <div class="wrap contact-foot__inner">
        <p>{{ $t("contact.copyright") }}</p>
        <router-link to="/" class="contact-foot__back">{{
          $t("contact.backFooter")
        }}</router-link>
      </div>
    </footer>
  </div>
</template>

<script>
import PsTopBar from "@/components/global/PsTopBar.vue";

export default {
  components: { PsTopBar },
  data() {
    return {
      submitting: false,
      formError: "",
      form: {
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      },
      errors: {},
      businessHours: [
        { dayKey: "monday", time: "08:00 - 17:00" },
        { dayKey: "tuesday", time: "08:00 - 17:00" },
        { dayKey: "wednesday", time: "08:00 - 17:00" },
        { dayKey: "thursday", time: "08:00 - 17:00" },
        { dayKey: "friday", time: "08:00 - 17:00" },
        { dayKey: "saturday", time: "09:00 - 12:00" },
        { dayKey: "sunday", time: "closed" },
      ],
      // Google Maps embed URL (Vientiane, Laos)
      mapEmbedUrl:
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3820.1234567890123!2d102.6331!3d17.9757!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x312467b553f8b923%3A0x1c3e9b6ce754a624!2zVmllbnRpYW5lLCBMYW9z!5e0!3m2!1sen!2sla!4v1234567890123!5m2!1sen!2sla",
    };
  },
  methods: {
    async submitForm() {
      // Manual validation
      this.errors = {};
      this.formError = "";
      let isValid = true;
      if (!this.form.name) {
        this.errors.name = [this.$t("field_required") || "ກະລຸນາຕື່ມຂໍ້ມູນ"];
        isValid = false;
      }
      if (!this.form.email) {
        this.errors.email = [this.$t("field_required") || "ກະລຸນາຕື່ມຂໍ້ມູນ"];
        isValid = false;
      } else if (
        !/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(this.form.email)
      ) {
        this.errors.email = [this.$t("invalid_email") || "ອີເມວບໍ່ຖືກຕ້ອງ"];
        isValid = false;
      }
      if (!this.form.subject) {
        this.errors.subject = [this.$t("field_required") || "ກະລຸນາຕື່ມຂໍ້ມູນ"];
        isValid = false;
      }
      if (!this.form.message) {
        this.errors.message = [this.$t("field_required") || "ກະລຸນາຕື່ມຂໍ້ມູນ"];
        isValid = false;
      }

      if (!isValid) {
        const first = Object.values(this.errors).flat()[0];
        this.formError = first;
        return;
      }

      this.submitting = true;
      try {
        // TODO: Implement API call to submit contact form
        // const { data } = await this.$http.post("contact", this.form);
        await new Promise((resolve) => setTimeout(resolve, 1200));
        this.$swal.fire({
          icon: "success",
          title: this.$t("success") || "ສຳເລັດ",
          text:
            this.$t("contact_form_success") ||
            "ຂອບໃຈທີ່ຕິດຕໍ່ພວກເຮົາ! ພວກເຮົາຈະຕອບກັບທ່ານໃນໄວໆນີ້.",
          showConfirmButton: false,
          timer: 3000,
        });
        this.form = {
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        };
      } catch (error) {
        this.$swal.fire({
          icon: "error",
          title: this.$t("error") || "ເກີດຂໍ້ຜິດພາດ",
          text:
            this.$t("contact_form_error") ||
            "ມີບັນຫາໃນການສົ່ງຂໍ້ຄວາມ. ກະລຸນາລອງໃໝ່.",
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.wrap {
  max-width: 80rem;
  margin: 0 auto;
  padding: 0 1rem;
}
@media (min-width: 640px) {
  .wrap {
    padding: 0 1.5rem;
  }
}
@media (min-width: 1024px) {
  .wrap {
    padding: 0 2rem;
  }
}

.contact-main {
  flex: 1;
  width: 100%;
  max-width: 72rem;
  padding-top: 56px;
  padding-bottom: 64px;
}

.contact-head {
  text-align: center;
  max-width: 42rem;
  margin: 0 auto;
}
.contact-head__highlight {
  background: var(--ps-lime);
  border: 1px solid #141021;
  border-radius: 8px;
  padding: 0 12px;
  display: inline-block;
}
.contact-head__sub {
  margin: 16px auto 0;
  max-width: 28rem;
  font-weight: 500;
  color: var(--ps-slate-500);
}

/* channels */
.contact-channels {
  margin-top: 48px;
  display: grid;
  gap: 20px;
}
@media (min-width: 768px) {
  .contact-channels {
    grid-template-columns: repeat(3, 1fr);
  }
}
.contact-channel {
  background: #fff;
  border: 1px solid #141021;
  border-radius: 24px;
  padding: 32px;
  text-align: center;
  box-shadow: 2px 2px 0 0 #141021;
  text-decoration: none;
  color: var(--ps-ink);
}
.contact-channel__icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.contact-channel__icon--green {
  background: #10b981;
}
.contact-channel__icon--brand {
  background: var(--ps-brand);
}
.contact-channel__icon--ink {
  background: var(--ps-ink);
}
.contact-channel__fb {
  font-weight: 900;
  font-size: 24px;
}
.contact-channel__title {
  margin: 16px 0 0;
  font-size: 1.125rem;
  font-weight: 900;
}
.contact-channel__desc {
  margin: 4px 0 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--ps-slate-500);
}

/* form + info */
.contact-grid {
  margin-top: 40px;
  display: grid;
  gap: 24px;
  align-items: start;
}
@media (min-width: 1024px) {
  .contact-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.contact-form {
  background: #fff;
  border: 1px solid #141021;
  border-radius: 24px;
  padding: 32px;
  box-shadow: var(--ps-shadow-hard);
}
.contact-form__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 900;
}
.contact-form__sub {
  margin: 4px 0 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--ps-slate-500);
}
.contact-form__body {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.contact-form__row {
  display: grid;
  gap: 16px;
}
@media (min-width: 640px) {
  .contact-form__row {
    grid-template-columns: 1fr 1fr;
  }
}
.ps-input--error {
  border-color: #dc2626;
}
.contact-form textarea.ps-input {
  resize: none;
  font-family: inherit;
}
.contact-form__error {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  color: #dc2626;
  text-align: center;
}
.contact-form__submit {
  padding: 1rem;
}

/* info cards */
.contact-info {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.contact-info-card {
  background: #fff;
  border: 1px solid #141021;
  border-radius: 24px;
  padding: 28px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  box-shadow: 2px 2px 0 0 #141021;
}
.contact-info-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--ps-brand-soft);
  color: var(--ps-brand);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.contact-info-card__icon--phone {
  background: #d1fae5;
  color: #047857;
}
.contact-info-card__title {
  margin: 0;
  font-weight: 900;
  font-size: 1rem;
}
.contact-info-card__desc {
  margin: 4px 0 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--ps-slate-500);
  line-height: 1.6;
}
.contact-info-cta {
  background: var(--ps-lime);
  border: 1px solid #141021;
  border-radius: 24px;
  padding: 28px;
  text-align: center;
  box-shadow: var(--ps-shadow-hard);
}
.contact-info-cta__text {
  margin: 0;
  font-weight: 900;
}
.contact-info-cta__btn {
  display: inline-block;
  margin-top: 16px;
  background: var(--ps-ink);
  color: #fff;
  font-weight: 900;
  padding: 12px 28px;
  border: 1px solid #141021;
  border-radius: 16px;
  text-decoration: none;
  transition: background-color 0.15s ease;
}
.contact-info-cta__btn:hover {
  background: var(--ps-brand);
}

/* map */
.contact-map {
  margin-top: 24px;
  background: #fff;
  border: 1px solid #141021;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: var(--ps-shadow-hard);
}
.contact-map__title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 24px;
  font-weight: 900;
  font-size: 1.0625rem;
  border-bottom: 1px solid var(--ps-slate-200);
}

/* bottom cards */
.contact-bottom {
  margin-top: 24px;
  display: grid;
  gap: 24px;
}
@media (min-width: 768px) {
  .contact-bottom {
    grid-template-columns: 1fr 1fr;
  }
}
.contact-bottom__card {
  background: #fff;
  border: 1px solid #141021;
  border-radius: 24px;
  padding: 28px;
  box-shadow: var(--ps-shadow-hard);
}
.contact-bottom__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 900;
  font-size: 1.0625rem;
}
.contact-bottom__hours {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.contact-bottom__hour {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 500;
}
.contact-bottom__hour-time {
  color: var(--ps-slate-500);
}
.contact-bottom__text {
  margin: 12px 0 0;
  font-size: 0.9375rem;
  line-height: 1.7;
  color: var(--ps-slate-600);
}
.contact-bottom__chip {
  display: inline-flex;
  align-items: center;
  margin-top: 12px;
  background: #dcfce7;
  color: #166534;
  font-weight: 700;
  font-size: 12px;
  border: 1px solid #141021;
  border-radius: 999px;
  padding: 4px 12px;
}

/* footer */
.contact-foot {
  border-top: 1px solid var(--ps-slate-300);
}
.contact-foot__inner {
  padding-top: 24px;
  padding-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  font-weight: 500;
  color: var(--ps-slate-400);
}
.contact-foot__back {
  font-weight: 700;
  color: var(--ps-ink);
  text-decoration: none;
  transition: color 0.15s ease;
}
.contact-foot__back:hover {
  color: var(--ps-brand);
}
</style>
